import {getCurrentUserId} from '@hconnect/common/authentication/getCurrentUserId'
import {useCallback, useMemo, useState} from 'react'
import {useNavigate} from 'react-router'

import {RiaDetailPanel} from '../components'
import {type SortDirection} from '../helpers'
import {
  useExportReport,
  useAddReportLink,
  useDeleteReportLink,
  useReportLinks,
  useReports,
  useRias,
  useRiskSurveyPermissions,
  useRiskSurveyQueryState
} from '../hooks'
import {RiskSurveyPlantPage} from '../pages'
import {RiaPriority, RiaStatus, type RiaListItemDto} from '../types'

import {ImportReportContainer} from './ImportReportContainer'

type RiskSurveyPlantContainerProps = {
  upmId: string
  onBack?: () => void
}

export const RiskSurveyPlantContainer = ({upmId, onBack}: RiskSurveyPlantContainerProps) => {
  const {state, setFilters, setPagination, setReport} = useRiskSurveyQueryState()
  const navigate = useNavigate()

  const {
    reportId: reportIdParam,
    page,
    pageSize,
    status: statusFilter,
    priority: priorityFilter
  } = state
  const reportId = reportIdParam ?? ''

  const [selectedRiaId, setSelectedRiaId] = useState<string | undefined>(undefined)
  const [importOpen, setImportOpen] = useState(false)
  const [sortBy, setSortBy] = useState<string | undefined>()
  const [sortDir, setSortDir] = useState<SortDirection>('asc')

  const {canManage} = useRiskSurveyPermissions(upmId)

  const {data: reportsData, isError: reportsError} = useReports({
    upmId,
    page: 1,
    pageSize: 100,
    includeArchived: !!reportId
  })
  const reports = reportsData?.items ?? []
  const selectedReportId = reportId || reports[0]?.id || ''

  const {
    data: riaData,
    isLoading: riasLoading,
    isError: riasError
  } = useRias({
    reportId: selectedReportId,
    status: statusFilter.length > 0 ? statusFilter : undefined,
    priority: priorityFilter.length > 0 ? priorityFilter : undefined,
    page,
    pageSize,
    sortBy,
    sortDir
  })

  const isError = reportsError || riasError

  const {mutate: exportReport} = useExportReport()
  const {data: links = []} = useReportLinks(selectedReportId)
  const {mutate: addReportLink, isPending: isAddingLink} = useAddReportLink()
  const {mutate: deleteReportLink, isPending: isDeletingLink} = useDeleteReportLink()

  const selectedReport = reports.find((r) => r.id === selectedReportId)
  const siteName = selectedReport?.siteName ?? ''
  const currentUserId = useMemo(() => getCurrentUserId(), [])
  const canManageLinks =
    canManage ||
    (!!currentUserId &&
      !!selectedReport?.uploadedByUserId &&
      selectedReport.uploadedByUserId === currentUserId)

  const handleReportChange = useCallback(
    (newReportId: string) => {
      setReport(newReportId)
      setSelectedRiaId(undefined)
    },
    [setReport]
  )

  const handleRiaSelect = useCallback((ria: RiaListItemDto) => {
    setSelectedRiaId((prev) => (prev === ria.id ? undefined : ria.id))
  }, [])

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    } else {
      void navigate(-1)
    }
  }, [navigate, onBack])

  const handleImport = useCallback(() => {
    setImportOpen(true)
  }, [])

  const handleExport = useCallback(() => {
    if (!selectedReportId) return
    exportReport({
      reportId: selectedReportId,
      fileName: `risk-survey-${siteName || 'report'}.xlsx`
    })
  }, [selectedReportId, siteName, exportReport])

  const handleSortChange = useCallback((newSortBy: string, newSortDir: SortDirection) => {
    setSortBy(newSortBy)
    setSortDir(newSortDir)
  }, [])

  const handleStatusFilterChange = useCallback(
    (values: RiaStatus[]) => setFilters({status: values}),
    [setFilters]
  )

  const handlePriorityFilterChange = useCallback(
    (values: RiaPriority[]) => setFilters({priority: values}),
    [setFilters]
  )

  const handleAddLink = useCallback(
    (data: {url: string; label: string}) => {
      if (!selectedReportId) return
      addReportLink({reportId: selectedReportId, data})
    },
    [addReportLink, selectedReportId]
  )

  const handleDeleteLink = useCallback(
    (linkId: string) => {
      if (!selectedReportId) return
      deleteReportLink({reportId: selectedReportId, linkId})
    },
    [deleteReportLink, selectedReportId]
  )

  return (
    <>
      <RiskSurveyPlantPage
        siteName={siteName}
        reports={reports}
        selectedReportId={selectedReportId}
        onReportChange={handleReportChange}
        riaData={riaData}
        isLoading={riasLoading}
        isError={isError}
        selectedRiaId={selectedRiaId}
        onRiaSelect={handleRiaSelect}
        onBack={handleBack}
        onImport={handleImport}
        onExport={handleExport}
        canManage={canManage}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={handlePriorityFilterChange}
        onPageChange={setPagination}
        onSortChange={handleSortChange}
        reportLinks={links}
        canManageLinks={canManageLinks}
        isAddingLink={isAddingLink}
        isDeletingLink={isDeletingLink}
        onAddLink={handleAddLink}
        onDeleteLink={handleDeleteLink}
        detailPanel={
          selectedRiaId ? (
            <RiaDetailPanel
              riaId={selectedRiaId}
              open={!!selectedRiaId}
              onClose={() => setSelectedRiaId(undefined)}
              canManage={canManage}
            />
          ) : undefined
        }
      />
      {importOpen && (
        <ImportReportContainer
          open={importOpen}
          onClose={() => setImportOpen(false)}
          upmId={upmId}
          siteName={siteName}
        />
      )}
    </>
  )
}
